/**
 * PackingStationsSection - Manage packing station instances and routing rules
 *
 * Allows coordinators to:
 * - Create named packing stations with routing rules
 * - Configure production method (POD/PAP/Mixed) and line count filters
 * - Mark stations as catch-all for unmatched orders
 * - Assign/unassign packers to stations
 *
 * Design: Matches existing settings page patterns with large touch targets
 * and high contrast for warehouse environment.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { usePackingStations } from '../hooks/usePackingStations';
import { trpc } from '@/lib/trpc';
import { parseHandoverRefusal } from '@/lib/trpc-error-utils';
import { useLocation as useLocationContext } from '@/contexts/LocationContext';
import {
  Plus,
  Trash2,
  Loader2,
  UserPlus,
  UserMinus,
  PackageCheck,
  ShieldOff,
} from 'lucide-react';
import type { PackingStationResponse, PackingRoutingRules } from '@threadheads/shared';

// ============================================================================
// HELPERS
// ============================================================================

function formatRules(routing: PackingRoutingRules | null): string {
  if (!routing) return 'All orders';
  if (routing.isCatchAll) return 'Catch-all (unmatched orders)';
  const parts: string[] = [];
  if (routing.productionMethod) parts.push(routing.productionMethod);
  if (routing.lineCount) parts.push(routing.lineCount === 'SINGLE' ? 'Single-line' : 'Multi-line');
  return parts.length > 0 ? parts.join(' + ') : 'All orders';
}

// ============================================================================
// CREATE STATION FORM
// ============================================================================

function CreateStationForm({ onCreated }: { onCreated: () => void }) {
  const { createStation, isCreating } = usePackingStations();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [productionMethod, setProductionMethod] = useState<string>('any');
  const [lineCount, setLineCount] = useState<string>('any');
  const [isCatchAll, setIsCatchAll] = useState(false);

  const handleCreate = useCallback(async () => {
    if (!name.trim()) return;

    const routing: PackingRoutingRules = {};
    if (isCatchAll) {
      routing.isCatchAll = true;
    } else {
      if (productionMethod !== 'any') routing.productionMethod = productionMethod as 'POD' | 'PAP' | 'MIXED';
      if (lineCount !== 'any') routing.lineCount = lineCount as 'SINGLE' | 'MULTI';
    }

    try {
      await createStation(name.trim(), routing);
      toast({ title: 'Station created', description: `"${name.trim()}" has been created`, duration: 3000 });
      setName('');
      setProductionMethod('any');
      setLineCount('any');
      setIsCatchAll(false);
      onCreated();
    } catch (error) {
      toast({
        title: 'Failed to create station',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
        duration: 5000,
      });
    }
  }, [name, productionMethod, lineCount, isCatchAll, createStation, toast, onCreated]);

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
      <div className="space-y-2">
        <Label htmlFor="station-name" className="text-sm font-medium">Station Name</Label>
        <Input
          id="station-name"
          placeholder='e.g., "Packing 1" or "PAP Station"'
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-11"
          maxLength={50}
        />
      </div>

      <div className="flex items-center gap-3">
        <Switch
          id="catch-all"
          checked={isCatchAll}
          onCheckedChange={setIsCatchAll}
          className="shrink-0"
        />
        <Label htmlFor="catch-all" className="text-sm">
          Catch-all station (handles orders not matched by other stations)
        </Label>
      </div>

      {!isCatchAll && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="production-method" className="text-sm font-medium">Production Method</Label>
            <Select value={productionMethod} onValueChange={setProductionMethod}>
              <SelectTrigger id="production-method" className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any" className="py-3">Any</SelectItem>
                <SelectItem value="POD" className="py-3">POD (Print on Demand)</SelectItem>
                <SelectItem value="PAP" className="py-3">PAP (Pick & Pack)</SelectItem>
                <SelectItem value="MIXED" className="py-3">Mixed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="line-count" className="text-sm font-medium">Line Count</Label>
            <Select value={lineCount} onValueChange={setLineCount}>
              <SelectTrigger id="line-count" className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any" className="py-3">Any</SelectItem>
                <SelectItem value="SINGLE" className="py-3">Single-line</SelectItem>
                <SelectItem value="MULTI" className="py-3">Multi-line</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          onClick={handleCreate}
          disabled={!name.trim() || isCreating}
          className="h-11 gap-2"
        >
          {isCreating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          Create Station
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// STATION CARD
// ============================================================================

function StationCard({
  station,
  packers,
}: {
  station: PackingStationResponse;
  packers: Array<{ id: string; email: string }>;
}) {
  const { updateStation, deleteStation, assignUser, unassignUser, isUpdating, isDeleting, isAssigning, isUnassigning } = usePackingStations();
  const { toast } = useToast();
  const { t } = useTranslation('settings');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPacker, setSelectedPacker] = useState<string>('');

  // Packers not yet assigned to this station
  const assignedProfileIds = new Set(station.assignedUsers.map(u => u.profileId));
  const availablePackers = packers.filter(p => !assignedProfileIds.has(p.id));

  // Older backends refused to remove the last hand over bench while a client
  // here was on Hand over; keep plain copy for that refusal.
  const describeStationError = useCallback((error: unknown, fallback?: string): string | undefined => {
    if (parseHandoverRefusal(error) === 'HANDOVER_LAST_BENCH') {
      return t('production.packingStations.handoverBench.lastBench');
    }
    return error instanceof Error ? error.message : fallback;
  }, [t]);

  const handleToggleActive = useCallback(async (active: boolean) => {
    try {
      await updateStation(station.id, { isActive: active });
      toast({
        title: 'Station updated',
        description: `"${station.name}" ${active ? 'enabled' : 'disabled'}`,
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Failed to update station',
        description: describeStationError(error, 'Please try again'),
        variant: 'destructive',
        duration: 5000,
      });
    }
  }, [station.id, station.name, updateStation, toast, describeStationError]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteStation(station.id);
      toast({ title: 'Station deleted', description: `"${station.name}" has been removed`, duration: 3000 });
      setShowDeleteDialog(false);
    } catch (error) {
      toast({
        title: 'Failed to delete station',
        description: describeStationError(error, 'Please try again'),
        variant: 'destructive',
        duration: 5000,
      });
    }
  }, [station.id, station.name, deleteStation, toast, describeStationError]);

  const handleAssign = useCallback(async () => {
    if (!selectedPacker) return;
    try {
      await assignUser(station.id, selectedPacker);
      const packer = packers.find(p => p.id === selectedPacker);
      toast({
        title: 'User assigned',
        description: `${packer?.email ?? 'User'} assigned to "${station.name}"`,
        duration: 3000,
      });
      setSelectedPacker('');
    } catch (error) {
      toast({
        title: 'Failed to assign user',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
        duration: 5000,
      });
    }
  }, [selectedPacker, station.id, station.name, packers, assignUser, toast]);

  const handleUnassign = useCallback(async (profileId: string, email: string) => {
    try {
      await unassignUser(profileId);
      toast({
        title: 'User unassigned',
        description: `${email} removed from "${station.name}"`,
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Failed to unassign user',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
        duration: 5000,
      });
    }
  }, [station.name, unassignUser, toast]);

  const handleToggleSkipVerification = useCallback(async (checked: boolean) => {
    try {
      await updateStation(station.id, { skipVerification: checked });
      toast({
        title: 'Station updated',
        description: `Item verification ${checked ? 'disabled' : 'enabled'} for "${station.name}"`,
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Failed to update station',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
        duration: 5000,
      });
    }
  }, [station.id, station.name, updateStation, toast]);

  const isBusy = isUpdating || isDeleting || isAssigning || isUnassigning;

  return (
    <div className="border rounded-lg p-4 space-y-3">
      {/* Station header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <PackageCheck className="h-5 w-5 text-muted-foreground shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <h4 className="font-medium truncate">{station.name}</h4>
            <p className="text-sm text-muted-foreground">{formatRules(station.routing)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Switch
            checked={station.isActive}
            onCheckedChange={handleToggleActive}
            disabled={isBusy}
            aria-label={`${station.isActive ? 'Disable' : 'Enable'} ${station.name}`}
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-destructive"
            onClick={() => setShowDeleteDialog(true)}
            disabled={isBusy}
            aria-label={`Delete ${station.name}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Separator />

      {/* Skip verification toggle */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <ShieldOff className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <Label className="text-sm font-medium">Skip Item Verification</Label>
            <p className="text-xs text-muted-foreground">
              Auto-verify items on scan: packers can print labels immediately
            </p>
          </div>
        </div>
        <Switch
          checked={station.skipVerification ?? false}
          onCheckedChange={handleToggleSkipVerification}
          disabled={isBusy}
          aria-label={`${station.skipVerification ? 'Require' : 'Skip'} verification for ${station.name}`}
        />
      </div>
      {station.skipVerification && (
        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
          <ShieldOff className="h-3 w-3 shrink-0" />
          Items won&apos;t be individually checked before shipping
        </p>
      )}

      <Separator />

      {/* Assigned users */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-muted-foreground">Assigned Packers</Label>
        {station.assignedUsers.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No packers assigned</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {station.assignedUsers.map(user => (
              <Badge
                key={user.profileId}
                variant="secondary"
                className="gap-1.5 pl-3 pr-1 py-1"
              >
                {user.email}
                <button
                  type="button"
                  onClick={() => handleUnassign(user.profileId, user.email)}
                  disabled={isBusy}
                  className="min-h-[28px] min-w-[28px] flex items-center justify-center hover:bg-muted rounded-full transition-colors"
                  aria-label={`Remove ${user.email}`}
                >
                  <UserMinus className="h-3.5 w-3.5" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {/* Assign packer */}
        {availablePackers.length > 0 && (
          <div className="flex gap-2 pt-1">
            <Select value={selectedPacker} onValueChange={setSelectedPacker}>
              <SelectTrigger className="h-10 flex-1">
                <SelectValue placeholder="Select a packer..." />
              </SelectTrigger>
              <SelectContent>
                {availablePackers.map(packer => (
                  <SelectItem key={packer.id} value={packer.id} className="py-2">
                    {packer.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={handleAssign}
              disabled={!selectedPacker || isBusy}
              className="h-10 gap-1.5"
            >
              {isAssigning ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4" />
              )}
              Assign
            </Button>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{station.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the station and unassign all packers.
              {station.assignedUsers.length > 0 && (
                <> {station.assignedUsers.length} packer{station.assignedUsers.length > 1 ? 's' : ''} will be unassigned.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="h-11 bg-destructive hover:bg-destructive/90">
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
// MAIN SECTION
// ============================================================================

export function PackingStationsSection() {
  const { selectedLocation } = useLocationContext();
  const { stations, isLoading, error } = usePackingStations();
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Get all users with Packer role for assignment dropdown
  const usersQuery = trpc.users.list.useQuery(undefined, {
    enabled: !!selectedLocation?.id,
    staleTime: 60_000,
  });

  // Filter to users with Packer role (or no role assignment restrictions)
  const packers = (usersQuery.data ?? [])
    .filter(u => u.is_active && (u.role === 'Packer' || u.role === 'Admin' || u.role === 'Warehouse Coordinator'))
    .map(u => ({ id: u.id, email: u.email ?? '' }));

  if (!selectedLocation?.id) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        Select a location to configure packing stations
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-destructive text-center py-4">
        Failed to load stations: {error.message}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <p className="text-sm text-muted-foreground/90">
          Create named packing stations with routing rules to direct different order types
          to different packers. When no stations are configured, all orders go to any packer.
        </p>
      </div>

      {/* Station list */}
      {stations.length > 0 && (
        <div className="space-y-3">
          {stations.map(station => (
            <StationCard
              key={station.id}
              station={station}
              packers={packers}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {stations.length === 0 && !showCreateForm && (
        <div className="text-center py-6 text-muted-foreground">
          <PackageCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No packing stations configured</p>
          <p className="text-xs mt-1">All orders will be available to any packer</p>
        </div>
      )}

      {/* Create form */}
      {showCreateForm ? (
        <CreateStationForm onCreated={() => setShowCreateForm(false)} />
      ) : (
        <Button
          variant="outline"
          onClick={() => setShowCreateForm(true)}
          className="h-11 gap-2 w-full"
        >
          <Plus className="h-4 w-4" />
          Add Packing Station
        </Button>
      )}
    </div>
  );
}
